import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["submitError"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { FORM_ERROR } from 'final-form';
import { Form, FormSpy } from 'react-final-form';
import { useIntl } from 'react-intl-next';
import { RequiredAsterisk } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import { CREATE_FORM_MAX_WIDTH_IN_PX, LINK_CREATE_FORM_POST_CREATE_FIELD } from '../../common/constants';
import messages from '../../common/messages';
import { useLinkCreateCallback } from '../../controllers/callback-context';
import { useExitWarningModal } from '../../controllers/exit-warning-modal-context';
import { useFormContext } from '../../controllers/form-context';
import { CreateFormFooter } from './form-footer';
import { CreateFormLoader } from './form-loader';
var formStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  maxWidth: "".concat(CREATE_FORM_MAX_WIDTH_IN_PX, "px"),
  padding: "0 0 ".concat("var(--ds-space-300, 24px)", " 0"),
  margin: "var(--ds-space-0, 0px)".concat(" auto")
});
var RESERVED_FIELDS = [LINK_CREATE_FORM_POST_CREATE_FIELD];
export var TEST_ID = 'link-create-form';
export var CreateForm = function CreateForm(_ref) {
  var children = _ref.children,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? TEST_ID : _ref$testId,
    onSubmit = _ref.onSubmit,
    onCancel = _ref.onCancel,
    isLoading = _ref.isLoading,
    hideFooter = _ref.hideFooter,
    hideRequiredFieldMessage = _ref.hideRequiredFieldMessage,
    initialValues = _ref.initialValues;
  var _useFormContext = useFormContext(),
    setFormErrorMessage = _useFormContext.setFormErrorMessage,
    formErrorMessage = _useFormContext.formErrorMessage,
    enableEditView = _useFormContext.enableEditView;
  var intl = useIntl();
  var _useExitWarningModal = useExitWarningModal(),
    setShouldShowWarning = _useExitWarningModal.setShouldShowWarning;
  var _useLinkCreateCallbac = useLinkCreateCallback(),
    onFailure = _useLinkCreateCallbac.onFailure;
  var handleSubmit = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
      var shouldEnableEditView, formData;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            shouldEnableEditView = data[LINK_CREATE_FORM_POST_CREATE_FIELD], formData = _objectWithoutProperties(data, [LINK_CREATE_FORM_POST_CREATE_FIELD].map(_toPropertyKey));
            /**
             * If form has post-create field set to trigger post-create edit
             * send this to the form context so we know what to do next
             * if submission is successful
             */
            enableEditView === null || enableEditView === void 0 || enableEditView(!!shouldEnableEditView);

            /**
             * This is the onSubmit handler provided by the plugin
             * It will be async, and it will likely involve awaiting `onCreate` (the adopters handler)
             */
            return _context.abrupt("return", onSubmit(formData));
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [onSubmit, enableEditView]);
  var handleSubmitWithErrorHandling = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _args2 = arguments;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          _context2.prev = 0;
          /**
           * Clear any error message that may have been set by async select fields
           * This will immediately remove any indication of an error, but the form likely will fail to submit,
           * it will be likely a 400 because the user probably could not set all fields anyway
           */
          setFormErrorMessage();
          _context2.next = 4;
          return handleSubmit.apply(void 0, _args2);
        case 4:
          return _context2.abrupt("return", _context2.sent);
        case 7:
          _context2.prev = 7;
          _context2.t0 = _context2["catch"](0);
          /**
           * Notify link create of failed experience
           */
          onFailure === null || onFailure === void 0 || onFailure(_context2.t0);

          /**
           * Return a generic message for react final form to render
           */
          return _context2.abrupt("return", _defineProperty({}, FORM_ERROR, intl.formatMessage(messages.genericErrorMessage)));
        case 11:
        case "end":
          return _context2.stop();
      }
    }, _callee2, null, [[0, 7]]);
  })), [handleSubmit, setFormErrorMessage, intl, onFailure]);
  var handleCancel = useCallback(function () {
    onCancel && onCancel();
  }, [onCancel]);
  if (isLoading) {
    return jsx(CreateFormLoader, null);
  }
  return jsx(Form, {
    onSubmit: handleSubmitWithErrorHandling,
    initialValues: initialValues,
    mutators: {
      setField: function setField(args, state, tools) {
        tools.changeValue(state, args[0].toString(), function () {
          return args[1];
        });
      }
    }
  }, function (_ref5) {
    var submitError = _ref5.submitError,
      formProps = _objectWithoutProperties(_ref5, _excluded);
    return jsx("form", {
      onSubmit: formProps.handleSubmit,
      name: "link-create-form",
      noValidate: true,
      "data-testid": testId,
      css: formStyles
    }, jsx(FormSpy, {
      subscription: {
        modified: true
      },
      onChange: function onChange(state) {
        // determine if any of the fields have been modified
        if (!state.modified) {
          setShouldShowWarning(false);
          return;
        }
        var isModified = Object.values(state.modified).some(function (value) {
          return value;
        });
        setShouldShowWarning(isModified);
      }
    }), !hideRequiredFieldMessage && jsx("p", null, intl.formatMessage(messages.requiredFieldInstruction), " ", jsx(RequiredAsterisk, null)), jsx(Box, null, children), !hideFooter && jsx(CreateFormFooter
    /**
     * We will prefer to render the error message connected to
     * react final form state (submitError) otherwise we can
     * default to the `formErrorMessage` that we sometimes use with our own
     * "form context" (only currently used for AsyncSelect field reporting failed loading)
     */, {
      formErrorMessage: submitError || formErrorMessage,
      handleCancel: handleCancel,
      testId: testId
    }));
  });
};